import { amountToDisplayValue } from '@billy/design-system'
import { Button, Callout, Card, SkeletonLoader, Typography, YStack } from '@shinetools/lumen-react'
import { colors } from '@shinetools/lumen-tokens/web/colors'
import { Trans, useTranslation } from 'react-i18next'

import { BillStatus } from '@features/bills/enums/billStatus'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useFetchBillsSummary } from '@views/bills/routes/BillsList/legacy/hooks/useFetchBillsSummary'

import * as Styled from './styles'

interface AmountProps {
  supplierId?: string
}

export const Amount = ({ supplierId }: AmountProps) => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const currencyId = organization?.baseCurrencyId
  const {
    data: billsSummary,
    isError,
    isLoading,
    refetch,
  } = useFetchBillsSummary({
    contactId: supplierId,
  })
  const overdueCount = billsSummary?.[BillStatus.Overdue]?.count ?? 0
  const unpaidCount = billsSummary?.[BillStatus.Unpaid]?.count ?? 0
  // Unpaid is every approved unpaid bill, including overdue ones. Do not add overdue on top.
  const amountDue = billsSummary?.[BillStatus.Unpaid]?.amount ?? 0

  const handleRetryButtonClick = () => {
    refetch()
  }

  if (isLoading) {
    return (
      <Styled.AmountCard>
        <Card variant="outlined">
          <YStack gap={4} padding={24}>
            <Typography.Text color={colors.content.neutral.bold} size="small">
              {t('suppliers.detail.amount_card.label.amount_due')}
            </Typography.Text>
            <SkeletonLoader height={32} shape="text" width={120} />
            <SkeletonLoader height={16} shape="text" width={120} />
            <SkeletonLoader height={16} shape="text" width={20} />
          </YStack>
        </Card>
      </Styled.AmountCard>
    )
  }

  // A failed summary has no amount. Showing 0 would look like the supplier is settled.
  if (isError && !billsSummary) {
    return (
      <Styled.AmountCard>
        <Card variant="outlined">
          <YStack gap={16} padding={24}>
            <Callout color="danger" message={t('suppliers.detail.error.fetch_amount')} />
            <Button label={t('suppliers.detail.error.retry')} onClick={handleRetryButtonClick} variant="tonal" />
          </YStack>
        </Card>
      </Styled.AmountCard>
    )
  }

  return (
    <Styled.AmountCard>
      <Card variant="outlined">
        <YStack gap={4} padding={24}>
          <Typography.Text color={colors.content.neutral.bold} size="small">
            {t('suppliers.detail.amount_card.label.amount_due')}
          </Typography.Text>
          <Typography.Header as="h2">{amountToDisplayValue(amountDue, false, currencyId)}</Typography.Header>
          <Typography.Text>
            <Trans
              components={{ strong: <strong /> }}
              count={unpaidCount}
              i18nKey="suppliers.detail.amount_card.text.purchases_to_pay"
            />
          </Typography.Text>
          <Typography.Text>
            <Trans
              components={{ strong: <strong /> }}
              count={overdueCount}
              i18nKey="suppliers.detail.amount_card.text.purchases_overdue"
            />
          </Typography.Text>
        </YStack>
      </Card>
    </Styled.AmountCard>
  )
}
